import { memo, useEffect, useRef } from 'react';

import type { BackdropVideo } from '@typings/content';

import styles from './HomeBackdrop.module.scss';

interface LoopVideoProps {
  readonly video: BackdropVideo;
  /** Plays while true. The caller decides when it can be seen. */
  readonly playing: boolean;
}

/** True when the visitor has asked the browser to save data. */
const prefersSaveData = (): boolean =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;

/**
 * A silent, looping background video that plays only when told to.
 *
 * Every glass surface above it re-filters its backdrop on each video
 * frame, so a video nobody can see would cost GPU time for nothing. With
 * Save-Data on it never plays and is never downloaded (`preload="none"`);
 * the poster stands in.
 */
export const LoopVideo = memo(({ video, playing }: LoopVideoProps) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (playing && !prefersSaveData()) {
      /* Muted is set as a property as well as an attribute: autoplay
         policies check the property, and React does not reflect it. */
      element.muted = true;
      element.play().catch(() => {
        /* Autoplay refused (power saving, policy): the poster remains. */
      });
    } else {
      element.pause();
    }
  }, [playing]);

  return (
    <video
      ref={ref}
      className={styles.video}
      poster={video.poster}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      tabIndex={-1}
    >
      {video.sources.map((source) => (
        <source key={source.src} src={source.src} type="video/mp4" media={source.media} />
      ))}
    </video>
  );
});

LoopVideo.displayName = 'LoopVideo';
